// S6.5 Phase 3 — derive the founder-readable label for the
// "Allow always for ___" button. The PATTERN itself (the canonical key
// stored in permission_allowlist) is computed by the BACKEND from the same
// toolInput, so the label here is for display only — clicking the button
// posts `{ decision: 'allow_always' }` and the backend re-derives via
// patternFor() which is the source of truth.
//
// Why mirror it on the frontend? founder-first §3 — the button copy must
// communicate exactly what scope is being granted, in plain English, so
// Santi never accidentally Allow-Always's something broader than he
// thinks. Out-of-sync labels would be a UX bug, not a security bug
// (backend pattern still wins), but we keep them aligned.

export interface AllowlistLabel {
  label: string; // founder-readable, e.g. "rm -rf node_modules"
  // True when this tool/input shape can produce a meaningful pattern. When
  // false, the "Allow always" button is hidden — the backend would reject
  // the click with BAD_PATTERN anyway, but hiding the button is cleaner UX.
  hasPattern: boolean;
}

export function allowlistLabelFor(
  toolName: string,
  toolInput: unknown,
): AllowlistLabel {
  if (!toolInput || typeof toolInput !== 'object') {
    return { label: '', hasPattern: false };
  }
  const o = toolInput as Record<string, unknown>;

  switch (toolName) {
    case 'Bash': {
      const cmd = typeof o.command === 'string' ? o.command : null;
      return cmd
        ? { label: trimForLabel(cmd, 80), hasPattern: true }
        : { label: '', hasPattern: false };
    }
    case 'Read':
    case 'Edit':
    case 'Write':
    case 'MultiEdit':
    case 'NotebookEdit': {
      const path = typeof o.file_path === 'string' ? o.file_path : null;
      return path
        ? { label: trimForLabel(path, 80), hasPattern: true }
        : { label: '', hasPattern: false };
    }
    case 'Glob':
    case 'Grep': {
      const pat = typeof o.pattern === 'string' ? o.pattern : null;
      return pat
        ? { label: trimForLabel(pat, 80), hasPattern: true }
        : { label: '', hasPattern: false };
    }
    case 'WebFetch': {
      const url = typeof o.url === 'string' ? o.url : null;
      if (!url) return { label: '', hasPattern: false };
      try {
        return { label: new URL(url).hostname, hasPattern: true };
      } catch {
        return { label: trimForLabel(url, 80), hasPattern: true };
      }
    }
    default: {
      // Unknown tools fall through to the JSON-stringify path on the
      // backend; the label here would be a JSON blob, which fails the
      // founder-first §8 filter ("no raw JSON in default surface"). Hide
      // the button for those — the user can still Allow-once.
      return { label: '', hasPattern: false };
    }
  }
}

function trimForLabel(s: string, max: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}
